import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { SessionStatusBadge } from "@/lib/attendance";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Play, CalendarPlus, ClipboardList, Clock, XCircle, CheckCircle2, History, AlertCircle,
} from "lucide-react";

export default function FacultyAttendance() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [today, setToday] = useState("");
    const [batches, setBatches] = useState([]);
    const [courses, setCourses] = useState([]);

    const [batchFilter, setBatchFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [unmarkedOnly, setUnmarkedOnly] = useState(false);

    const [startOpen, setStartOpen] = useState(false);
    const [startForm, setStartForm] = useState({ batch_id: "", course_id: "", mode: "offline", notes: "" });
    const [busy, setBusy] = useState(false);

    const [pastOpen, setPastOpen] = useState(false);
    const emptyPast = {
        batch_id: "", course_id: "", session_date: "", start_time: "10:00",
        end_time: "", mode: "offline", notes: "",
    };
    const [pastForm, setPastForm] = useState(emptyPast);

    const [cancelling, setCancelling] = useState(null);
    const [cancelReason, setCancelReason] = useState("");

    const load = async () => {
        const params = new URLSearchParams();
        if (batchFilter) params.append("batch_id", batchFilter);
        if (statusFilter) params.append("status", statusFilter);
        if (unmarkedOnly) params.append("unmarked", "true");
        try {
            const { data } = await api.get(`/sessions?${params}`);
            setSessions(data.items);
            setToday(data.today);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load sessions");
        }
    };
    useEffect(() => { load(); }, [batchFilter, statusFilter, unmarkedOnly]); // eslint-disable-line
    useEffect(() => {
        api.get("/batches").then(r => setBatches(r.data.items)).catch(() => {});
        api.get("/courses").then(r => setCourses(r.data.items)).catch(() => {});
    }, []);

    // Only courses attached to the chosen batch may host a session — the backend
    // rejects anything else with a 400.
    const coursesFor = (batchId) => {
        const b = batches.find(x => x.id === batchId);
        const ids = b?.course_ids || [];
        return courses.filter(c => ids.includes(c.id));
    };

    const startClass = async () => {
        if (!startForm.batch_id || !startForm.course_id) return;
        setBusy(true);
        try {
            const { data } = await api.post("/sessions/start", startForm);
            toast.success(data.reopened ? "Reopened the class already in progress" : "Class started");
            setStartOpen(false);
            setStartForm({ batch_id: "", course_id: "", mode: "offline", notes: "" });
            navigate(`/faculty/attendance/${data.session.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start the class");
        } finally { setBusy(false); }
    };

    const logPastClass = async () => {
        if (!pastForm.batch_id || !pastForm.course_id || !pastForm.session_date) return;
        setBusy(true);
        try {
            const body = { ...pastForm, status: "completed" };
            if (!body.end_time) delete body.end_time;
            const { data } = await api.post("/sessions", body);
            toast.success("Past class logged — mark the roster now");
            setPastOpen(false);
            setPastForm(emptyPast);
            navigate(`/faculty/attendance/${data.session.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not log the class");
        } finally { setBusy(false); }
    };

    const cancelSession = async () => {
        if (!cancelling || !cancelReason.trim()) return;
        try {
            await api.post(`/sessions/${cancelling.id}/cancel`, { reason: cancelReason });
            toast.success("Session cancelled — it will not count as absences");
            setCancelling(null); setCancelReason("");
            load();
        } catch (err) {
            // Cancelling a submitted session obeys the 24-hour edit window, so the
            // backend's reason is the useful message here.
            toast.error(err.response?.data?.detail || "Could not cancel");
        }
    };

    const todays = sessions.filter(s => s.session_date === today);
    const unmarked = sessions.filter(s => s.is_unmarked && s.status !== "scheduled");

    return (
        <div className="space-y-6" data-testid="faculty-attendance-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Classes</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Attendance</h1>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Dialog open={pastOpen} onOpenChange={setPastOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="rounded-xl" data-testid="log-past-class-btn">
                                <CalendarPlus className="h-4 w-4 mr-2" /> Log past class
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Log a past class</DialogTitle>
                                <DialogDescription>
                                    For a class that already happened. It is recorded as backdated, with your name and the time you entered it.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Batch</Label>
                                    <Select value={pastForm.batch_id} onValueChange={v => setPastForm({ ...pastForm, batch_id: v, course_id: "" })}>
                                        <SelectTrigger data-testid="past-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                                        <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Course</Label>
                                    <Select value={pastForm.course_id} onValueChange={v => setPastForm({ ...pastForm, course_id: v })} disabled={!pastForm.batch_id}>
                                        <SelectTrigger data-testid="past-course-select"><SelectValue placeholder={pastForm.batch_id ? "Select course" : "Pick a batch first"} /></SelectTrigger>
                                        <SelectContent>{coursesFor(pastForm.batch_id).map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <Label>Date</Label>
                                        <Input type="date" max={today} value={pastForm.session_date}
                                            onChange={e => setPastForm({ ...pastForm, session_date: e.target.value })} data-testid="past-date-input" />
                                    </div>
                                    <div>
                                        <Label>Start</Label>
                                        <Input type="time" value={pastForm.start_time} onChange={e => setPastForm({ ...pastForm, start_time: e.target.value })} data-testid="past-start-input" />
                                    </div>
                                    <div>
                                        <Label>End</Label>
                                        <Input type="time" value={pastForm.end_time} onChange={e => setPastForm({ ...pastForm, end_time: e.target.value })} data-testid="past-end-input" />
                                    </div>
                                </div>
                                <div>
                                    <Label>Mode</Label>
                                    <Select value={pastForm.mode} onValueChange={v => setPastForm({ ...pastForm, mode: v })}>
                                        <SelectTrigger data-testid="past-mode-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="offline">Offline (in class)</SelectItem>
                                            <SelectItem value="online">Online</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                    <Textarea rows={2} value={pastForm.notes} onChange={e => setPastForm({ ...pastForm, notes: e.target.value })} data-testid="past-notes-input" />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={logPastClass} disabled={busy || !pastForm.batch_id || !pastForm.course_id || !pastForm.session_date}
                                    className="mx-btn-primary" data-testid="past-submit-btn">Log class &amp; mark</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={startOpen} onOpenChange={setStartOpen}>
                        <DialogTrigger asChild>
                            <Button className="mx-btn-primary" data-testid="start-class-btn">
                                <Play className="h-4 w-4 mr-2" /> Start class
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Start a class now</DialogTitle>
                                <DialogDescription>
                                    Creates a session for today at the current time. If this batch already has a class in progress, you'll be taken back to it.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Batch</Label>
                                    <Select value={startForm.batch_id} onValueChange={v => setStartForm({ ...startForm, batch_id: v, course_id: "" })}>
                                        <SelectTrigger data-testid="start-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                                        <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Course</Label>
                                    <Select value={startForm.course_id} onValueChange={v => setStartForm({ ...startForm, course_id: v })} disabled={!startForm.batch_id}>
                                        <SelectTrigger data-testid="start-course-select"><SelectValue placeholder={startForm.batch_id ? "Select course" : "Pick a batch first"} /></SelectTrigger>
                                        <SelectContent>{coursesFor(startForm.batch_id).map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Mode</Label>
                                    <Select value={startForm.mode} onValueChange={v => setStartForm({ ...startForm, mode: v })}>
                                        <SelectTrigger data-testid="start-mode-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="offline">Offline (in class)</SelectItem>
                                            <SelectItem value="online">Online</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={startClass} disabled={busy || !startForm.batch_id || !startForm.course_id}
                                    className="mx-btn-primary" data-testid="start-submit-btn">Start &amp; open roster</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Today + unmarked summary */}
            <div className="grid gap-4 sm:grid-cols-3">
                <Card className="mx-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                            <Clock className="h-3.5 w-3.5" /> Today
                        </div>
                        <div className="font-display text-3xl mt-2" data-testid="today-session-count">{todays.length}</div>
                        <div className="text-xs text-muted-foreground mt-1">session{todays.length === 1 ? "" : "s"}</div>
                    </CardContent>
                </Card>
                <Card className="mx-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                            <AlertCircle className="h-3.5 w-3.5" /> Unmarked
                        </div>
                        <div className={`font-display text-3xl mt-2 ${unmarked.length ? "text-[#E11D48]" : ""}`} data-testid="unmarked-count">{unmarked.length}</div>
                        <div className="text-xs text-muted-foreground mt-1">need attendance</div>
                    </CardContent>
                </Card>
                <Card className="mx-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                            <History className="h-3.5 w-3.5" /> All sessions
                        </div>
                        <div className="font-display text-3xl mt-2">{sessions.length}</div>
                        <div className="text-xs text-muted-foreground mt-1">in view</div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
                <Select value={batchFilter || "ALL"} onValueChange={v => setBatchFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-52" data-testid="attendance-batch-filter"><SelectValue placeholder="All batches" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">All my batches</SelectItem>
                        {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={statusFilter || "ALL"} onValueChange={v => setStatusFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-44" data-testid="attendance-status-filter"><SelectValue placeholder="Any status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">Any status</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant={unmarkedOnly ? "default" : "outline"} className={unmarkedOnly ? "mx-btn-primary" : "rounded-xl"}
                    onClick={() => setUnmarkedOnly(v => !v)} data-testid="unmarked-toggle">
                    Unmarked only
                </Button>
            </div>

            <Card className="mx-card">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date &amp; time</TableHead>
                                <TableHead>Batch / course</TableHead>
                                <TableHead>Faculty</TableHead>
                                <TableHead>Mode</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Marked</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sessions.map(s => (
                                <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                                    <TableCell>
                                        <div className="font-semibold text-sm">
                                            {s.session_date}{s.session_date === today && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#726AF0]">Today</span>}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {s.start_time}{s.end_time ? `–${s.end_time}` : ""}
                                        </div>
                                        {s.is_backdated && (
                                            <Badge variant="outline" className="mt-1 text-[10px] border-[#FFA81B] text-[#FFA81B]" data-testid={`backdated-badge-${s.id}`}>
                                                Backdated
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="font-semibold text-sm">{s.batch_name}</div>
                                        <div className="text-xs text-muted-foreground">{s.course_title}</div>
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{s.faculty_name || "—"}</TableCell>
                                    <TableCell className="text-sm capitalize">{s.mode}</TableCell>
                                    <TableCell><SessionStatusBadge status={s.status} /></TableCell>
                                    <TableCell>
                                        {s.status === "cancelled" ? (
                                            <span className="text-xs text-muted-foreground">—</span>
                                        ) : s.is_unmarked ? (
                                            <Badge className="bg-[#E11D48] hover:bg-[#E11D48] text-[10px]" data-testid={`unmarked-badge-${s.id}`}>Unmarked</Badge>
                                        ) : (
                                            <span className="text-sm">{s.marked_count} marked</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-2">
                                            <Button size="sm" variant="outline" className="rounded-xl"
                                                onClick={() => navigate(`/faculty/attendance/${s.id}`)} data-testid={`open-session-${s.id}`}>
                                                <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                                                {s.status === "cancelled" ? "View" : s.is_unmarked ? "Mark" : "Review"}
                                            </Button>
                                            {/* Offered for completed sessions too: the backend allows the
                                                marking faculty to cancel one inside the 24-hour window, and
                                                refuses with a reason after it. */}
                                            {s.status !== "cancelled" && (
                                                <Button size="sm" variant="ghost" className="text-[#E11D48] hover:text-[#E11D48]"
                                                    onClick={() => { setCancelling(s); setCancelReason(""); }}
                                                    aria-label="Cancel session" data-testid={`cancel-session-${s.id}`}>
                                                    <XCircle className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sessions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                                        <CheckCircle2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                        No sessions here. Use “Start class” when a class begins.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={!!cancelling} onOpenChange={v => { if (!v) { setCancelling(null); setCancelReason(""); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel this session?</DialogTitle>
                        <DialogDescription>
                            A cancelled session is excluded from all attendance calculations — it will never count as an absence for anyone.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label>Reason</Label>
                        <Textarea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                            placeholder="e.g. faculty unwell, power outage, holiday" data-testid="cancel-reason-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-xl" onClick={() => setCancelling(null)}>Keep it</Button>
                        <Button onClick={cancelSession} disabled={!cancelReason.trim()}
                            className="bg-[#E11D48] hover:bg-[#E11D48] text-white rounded-xl" data-testid="cancel-confirm-btn">
                            Cancel session
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
